import { useHistory, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useCreateMutation } from "./Settings.state";
import {
  Checkbox,
  Input,
  RadioGroup,
  RowInfo,
  Spinner,
  Textarea,
  TimingSection,
} from "../common";
import { Button, ButtonWrapper, Col, Row } from "../App.styles";
import {
  DescriptionModal,
  EntryCodeModal,
  GameModeModal,
  LegalModal,
  NameModal,
  PrimaryEmailModal,
  TimezonesModal,
} from "./modals";
import { useBackwardCompatibleNubs, useNubsQuery, useSyncNubsWithGameImage } from "./Nubs.state";
import MediaSection from "./MediaSection";
import Timezone from "./Timezone";
import { useMemo } from "react";

const TimingWrapper = styled.div`
  height: auto;
  max-height: ${({ show }) => (show ? "400px" : "0")};
  transition: max-height linear 0.5s;
  overflow: hidden;
`;

const MandatoryWithMedia = ({ useQuery }) => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  const [ game ] = useQuery();
  const { finish_at, start_anytime, start_at, tz } = game;
  const history = useHistory();
  const { isCreating, create } = useCreateMutation();
  const useMediaQuery = (config) => useNubsQuery(config);

  const date =
    start_at || finish_at ? new Date(start_at || finish_at) : new Date();

  const showTimingWrapper = useMemo(() => {
    return start_anytime === null || parseInt(start_anytime) === 0
  }, [start_anytime])

  const onCreate = async () => {
    const { data } = await create();
    history.push(`/tasks/organize/${data.id}`);
  };

  useBackwardCompatibleNubs()
  useSyncNubsWithGameImage()

  return (
    <>
      <RowInfo help={<NameModal />}>
        <Input
            help={t('Give your game a name. This will be visible to the public via search.')}
            label={t('Name')}
            name="name"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<DescriptionModal />}>
        <Textarea
            help={t('This is the main message to players at the entry gate to your game.')}
            label={t('Preview')}
            model="games"
            name="description"
            useQuery={useQuery}
            mode="clean"
            rows="2"
          />
      </RowInfo>
      <MediaSection useQuery={useMediaQuery} id={game_id} />
      <RowInfo help={<EntryCodeModal />}>
        <Input
            help={t('This is the secret or password each player is required to provide before joining a game.')}
            label={t('Game Entry Code')}
            name="code"
            useQuery={useQuery}
            minLength={3}
          />
      </RowInfo>
      <RowInfo help={<GameModeModal />}>
        <RadioGroup
            defaultValue={1}
            label={t('Game Mode')}
            name="start_anytime"
            options={[
              { value: 0, label: t('Scheduled') },
              { value: 2, label: t('Stopped') },
            ]}
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo rowClassName={showTimingWrapper ? '' : 'd-none'}>
        <TimingWrapper
          show={showTimingWrapper}
        >
          <TimingSection
            startHelp={t('If you set a start date, challenges will be hidden until then.')}
            startLabel={t('Start Date and Time')}
            finishHelp={t('If you set a finish date, challenges will be hidden afterwards.')}
            finishLabel={t('Finish Date and Time')}
            startName="start_at"
            finishName="finish_at"
            timezone={tz}
            useQuery={useQuery}
          />
        </TimingWrapper>
      </RowInfo>
      <RowInfo rowClassName="force-vspace" help={<TimezonesModal />}>
        <Timezone
            date={date}
            label={t('Timezone')}
            name="tz"
            offsetName="time_zone"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<PrimaryEmailModal />}>
        <Input
            help={t('If we need to reach someone, we will contact this address.')}
            label={t('Primary Email')}
            name="primary_email"
            useQuery={useQuery}
          />
      </RowInfo>
      <RowInfo help={<LegalModal />}>
        <Checkbox
            disabled={game_id !== "new"}
            label={t('Legal')}
            name="signed_legal"
            useQuery={useQuery}
            text={
              <>
                {t('I agree to the Social Scavenger')}{" "}
                <a href="/terms" dangerouslySetInnerHTML={{__html: t('Terms of Use')}} />
              </>
            }
          />
      </RowInfo>
      <Row>
        <Col>
          <ButtonWrapper>
            {game_id === "new" && (
              <Button color="primary" onClick={onCreate}>
                {t('Save')}{" "}
                {isCreating ? (
                  <Spinner size="0.875rem" />
                ) : (
                  <FontAwesomeIcon icon={faAngleDoubleRight} />
                )}
              </Button>
            )}
          </ButtonWrapper>
        </Col>
      </Row>
    </>
  );
};

export default MandatoryWithMedia;